import React, { useState, useEffect } from 'react';
import { Search, FileSpreadsheet, AlertTriangle, Loader2, Users, List } from 'lucide-react';
import { metricsAPI, handleMetricsAPIError } from './api';
import LoanPortfolioView from './LoanPortfolioView';

const BookPortfolioTab = ({ isDark }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [portfolioData, setPortfolioData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLoanView, setShowLoanView] = useState(false);

  useEffect(() => {
    fetchBookPortfolio();
  }, []);

  useEffect(() => {
    if (portfolioData) {
      handleSearch(searchQuery);
    }
  }, [portfolioData, searchQuery]);

  const fetchBookPortfolio = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await metricsAPI.getBookPortfolio();

      if (response.success) {
        setPortfolioData(response.data);
        setFilteredData(response.data.groups);
      } else {
        setError('Failed to load book portfolio data');
      }
    } catch (err) {
      setError(handleMetricsAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!portfolioData) return;

    if (query.trim() === '') {
      setFilteredData(portfolioData.groups);
    } else {
      const filtered = portfolioData.groups.filter(group =>
        group.group_name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredData(filtered);
    }
  };

  const getSurvivalColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  const getRiskColor = (risk) => {
    return risk < 0 ? 'text-red-600' : 'text-green-600';
  };

  const exportToExcel = () => {
    alert('Export to Excel functionality would be implemented here');
    // TODO: Implement Excel export
  };

  // If showing loan view, render that instead
  if (showLoanView) {
    return <LoanPortfolioView isDark={isDark} onBack={() => setShowLoanView(false)} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Loading book portfolio...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
        <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
        <button 
          onClick={fetchBookPortfolio}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!portfolioData || !portfolioData.groups || portfolioData.groups.length === 0) {
    return (
      <div className={`rounded-lg p-6 text-center ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
        <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
        <p className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          No group portfolio data available
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Book Portfolio
          </h2>
          
          {/* View Toggle Button */}
          <button
            onClick={() => setShowLoanView(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <List className="w-5 h-5" />
            View Member Loans
          </button>
        </div>
        
        {/* Risk Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Groups</div>
            <div className="text-2xl font-bold text-blue-600">
              {portfolioData.risk_summary.total_groups}
            </div>
          </div>
          <div className={`p-4 rounded-lg ${isDark ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'}`}>
            <div className={`text-sm mb-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>Low Risk</div>
            <div className="text-2xl font-bold text-green-600">
              {portfolioData.risk_summary.low_risk_groups}
            </div>
          </div>
          <div className={`p-4 rounded-lg ${isDark ? 'bg-orange-900/20 border border-orange-800' : 'bg-orange-50 border border-orange-200'}`}>
            <div className={`text-sm mb-1 ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>Medium Risk</div>
            <div className="text-2xl font-bold text-orange-600">
              {portfolioData.risk_summary.medium_risk_groups}
            </div>
          </div>
          <div className={`p-4 rounded-lg ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
            <div className={`text-sm mb-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>High Risk</div>
            <div className="text-2xl font-bold text-red-600">
              {portfolioData.risk_summary.high_risk_groups}
            </div>
          </div>
        </div>

        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search groups..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg transition-colors ${
                isDark 
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>
          <button
            onClick={exportToExcel}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FileSpreadsheet className="w-5 h-5" />
            Export Excel
          </button>
        </div>
      </div>

      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Group Name
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Total Members
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Loans
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Advance Loans
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Savings
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Risk Amount
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Risk %
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Viability Score
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {filteredData.map((group, index) => {
                return (
                  <tr key={index} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4">
                      <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {group.group_name}
                      </div>
                      {group.location !== 'N/A' && (
                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          {group.location}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
                        isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {group.total_members}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-blue-600">
                      KSh {group.total_loans.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-orange-600">
                      KSh {group.total_advance_loans.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-green-600">
                      KSh {group.total_savings.toLocaleString()}
                    </td>
                    <td className={`px-6 py-4 text-right font-bold ${getRiskColor(group.risk_amount)}`}>
                      KSh {group.risk_amount.toLocaleString()}
                    </td>
                    <td className={`px-6 py-4 text-right font-bold ${getRiskColor(group.risk_amount)}`}>
                      {group.risk_percentage}%
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className={`text-2xl font-bold ${getSurvivalColor(group.viability_score)}`}>
                          {group.viability_score}%
                        </div>
                        {group.viability_score < 60 && group.viability_score > 0 && (
                          <AlertTriangle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                      {group.viability_score > 0 && (
                        <div className={`w-full rounded-full h-2 mt-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          <div 
                            className={`h-2 rounded-full ${
                              group.viability_score >= 80 ? 'bg-green-500' : 
                              group.viability_score >= 60 ? 'bg-orange-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${group.viability_score}%` }}
                          ></div>
                        </div>
                      )}
                      {group.viability_score === 0 && (
                        <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          Pending calculation
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className={`border-t-2 ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
              <tr>
                <td className={`px-6 py-4 font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} colSpan="2">
                  TOTAL
                </td>
                <td className="px-6 py-4 text-right font-bold text-blue-600">
                  KSh {portfolioData.totals.total_loans.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-right font-bold text-orange-600">
                  KSh {portfolioData.totals.total_advance_loans.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-right font-bold text-green-600">
                  KSh {portfolioData.totals.total_savings.toLocaleString()}
                </td>
                <td className={`px-6 py-4 text-right font-bold ${getRiskColor(portfolioData.totals.total_risk_amount)}`}>
                  KSh {portfolioData.totals.total_risk_amount.toLocaleString()}
                </td>
                <td colSpan="2" className={`px-6 py-4 text-center font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Average Viability: {portfolioData.totals.average_viability.toFixed(1)}%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {filteredData.length === 0 && searchQuery && (
        <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          No groups found matching your search.
        </div>
      )}
    </div>
  );
};

export default BookPortfolioTab;